import { useEffect } from "react";

/**
 * Near-real-time shell data without three competing polling loops.
 *
 * Notifications, Inbox unread count and Action Centre attention all subscribe
 * to this one foreground cadence. Each subscriber still owns its authoritative
 * endpoint; this module only decides when the small shell summaries should be
 * refreshed. Hidden tabs skip work and refresh immediately when they become
 * visible again.
 */
export const LIVE_SHELL_REFRESH_MS = 15_000;

type LiveShellListener = () => void;

const listeners = new Set<LiveShellListener>();
let timer: number | null = null;
let listeningToVisibility = false;

function visible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function refreshLiveShellNow(): void {
  if (!visible()) return;
  for (const listener of [...listeners]) listener();
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") refreshLiveShellNow();
}

function start(): void {
  if (typeof window === "undefined" || timer !== null) return;
  timer = window.setInterval(refreshLiveShellNow, LIVE_SHELL_REFRESH_MS);
  window.addEventListener("online", refreshLiveShellNow);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
    listeningToVisibility = true;
  }
}

function stop(): void {
  if (typeof window !== "undefined" && timer !== null) {
    window.clearInterval(timer);
    window.removeEventListener("online", refreshLiveShellNow);
  }
  timer = null;
  if (typeof document !== "undefined" && listeningToVisibility) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
  listeningToVisibility = false;
}

export function subscribeLiveShellRefresh(listener: LiveShellListener): () => void {
  listeners.add(listener);
  start();

  let subscribed = true;
  return () => {
    if (!subscribed) return;
    subscribed = false;
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

/** Subscribe a stable reload callback to the shared shell refresh source. */
export function useLiveShellRefresh(listener: LiveShellListener): void {
  useEffect(() => subscribeLiveShellRefresh(listener), [listener]);
}
